/**
 * SK-135: write an outcome block's text to the clipboard, the way every
 * browser we support accepts it.
 *
 * - `text/plain` always; `text/html` too when there is any (a rich body), so
 *   pasting into Word or Gmail keeps its headings and lists.
 * - The `ClipboardItem` is built synchronously, inside the click, with
 *   promise-valued blobs: Safari refuses a clipboard write that starts after an
 *   `await`, so the HTML is passed as a promise and rendered inside the write.
 * - A browser without `ClipboardItem` gets `writeText` (plain text only).
 *
 * Resolves `false` instead of throwing when the browser refuses the write, so
 * the caller can say so ("Copy failed. Select the text manually.").
 */
export const writeToClipboard = async (
  plain: string,
  html?: Promise<string>,
): Promise<boolean> => {
  const clipboard =
    typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  if (!clipboard) {
    return false;
  }
  try {
    if (typeof ClipboardItem === 'undefined') {
      await clipboard.writeText(plain);
      return true;
    }
    const blobs: Record<string, Promise<Blob>> = {
      'text/plain': Promise.resolve(new Blob([plain], { type: 'text/plain' })),
    };
    if (html) {
      blobs['text/html'] = html.then(
        (markup) => new Blob([markup], { type: 'text/html' }),
      );
    }
    await clipboard.write([new ClipboardItem(blobs)]);
    return true;
  } catch {
    return false;
  }
};
